// Navigation Badge Component
// Status and notification badges for navigation items

import React from 'react';
import { useTheme } from '../../design-system/ThemeProvider';

interface NavigationBadgeProps {
  content: string | number;
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'sm' | 'md';
}

export const NavigationBadge: React.FC<NavigationBadgeProps> = ({
  content,
  variant = 'default',
  size = 'sm'
}) => {
  const { theme } = useTheme();

  const getVariantStyles = () => {
    switch (variant) {
      case 'success':
        return {
          backgroundColor: theme.colors.success[100],
          color: theme.colors.success[800],
          border: `1px solid ${theme.colors.success[200]}`
        };
      case 'warning':
        return {
          backgroundColor: theme.colors.warning[100],
          color: theme.colors.warning[800],
          border: `1px solid ${theme.colors.warning[200]}`
        };
      case 'danger':
        return {
          backgroundColor: theme.colors.danger[100],
          color: theme.colors.danger[800],
          border: `1px solid ${theme.colors.danger[200]}`
        };
      case 'info':
        return {
          backgroundColor: theme.colors.primary[100],
          color: theme.colors.primary[800],
          border: `1px solid ${theme.colors.primary[200]}`
        };
      default:
        return {
          backgroundColor: theme.semanticColors.surface.tertiary,
          color: theme.semanticColors.text.secondary,
          border: `1px solid ${theme.semanticColors.border.primary}`
        };
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'md':
        return {
          fontSize: theme.typography.fontSize.sm,
          padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
          minWidth: '24px',
          height: '24px'
        };
      default:
        return {
          fontSize: theme.typography.fontSize.xs,
          padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
          minWidth: '20px',
          height: '20px'
        };
    }
  };

  // Special handling for "live" indicator
  if (content === 'live') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing[1],
          fontSize: theme.typography.fontSize.xs,
          color: theme.colors.success[600],
          fontWeight: theme.typography.fontWeight.medium
        }}
      >
        <div
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: theme.colors.success[500],
            animation: 'pulse 2s infinite'
          }}
        />
        Live
      </div>
    );
  }

  return (
    <div
      style={{
        ...getVariantStyles(),
        ...getSizeStyles(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: theme.borderRadius.full,
        fontWeight: theme.typography.fontWeight.medium,
        lineHeight: 1
      }}
    >
      {content}
    </div>
  );
};